<!--
  TSE-Datenexport (#776).

  Two cards: the request form, then the runs. Both reflow rather than switch layouts — the two date fields are
  side by side from 320px up because they are narrow, and every row inside a run card stacks below `sm`.

  There is no register picker and no archive-format field. Neither is an omission: this export is never
  narrowed to a register, and the interface that produces it offers no container choice at all.
-->
<section class="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:gap-6 sm:py-8">
  <header class="flex flex-col gap-1">
    <h1 class="text-2xl font-bold tracking-tight text-gray-900">TSE-Datenexport</h1>
    <p class="text-sm text-gray-500">
      Erstellen Sie für einen Zeitraum den Datenexport Ihrer technischen Sicherheitseinrichtung: die
      signierten Aufzeichnungen, die eine Prüfung neben den Kassendaten anfordern kann. Fertige Exporte
      bleiben hier stehen und können erneut heruntergeladen werden.
    </p>
  </header>

  @if (message(); as text) {
    <p class="rounded-xl px-4 py-3 text-sm"
       [class.bg-green-50]="messageIsSuccess()"
       [class.text-green-700]="messageIsSuccess()"
       [class.bg-red-50]="!messageIsSuccess()"
       [class.text-red-700]="!messageIsSuccess()"
       [attr.role]="messageIsSuccess() ? 'status' : 'alert'">{{ text }}</p>
  }

  <!-- ── Neuen Export erstellen ─────────────────────────────────────────────────────────────────────── -->
  <section class="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
    <h2 class="text-base font-bold tracking-tight text-gray-900">Neuen Export erstellen</h2>

    <div class="mt-4 grid grid-cols-2 gap-2 sm:gap-3">
      <!--
        min-w-0 + an explicit height + appearance-none: a native date input in a grid overflows its card on
        iOS Safari and collapses when empty (Bug #646).
      -->
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="tse-export-from">Von</label>
        <input type="date" id="tse-export-from" [max]="today()"
               [ngModel]="from()" (ngModelChange)="from.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="tse-export-to">Bis</label>
        <input type="date" id="tse-export-to" [max]="today()"
               [ngModel]="to()" (ngModelChange)="to.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
    </div>

    <!--
      Sagt zwei Dinge, die der Leser sonst erst aus einer Absage erfährt: warum hier höchstens ein Jahr geht
      (der DSFinV-K-Export nebenan erlaubt zehn), und warum ein Zeitraum mehrere Dateien ergeben kann.
    -->
    <p class="mt-3 text-xs text-gray-500">
      Ein Export umfasst höchstens ein Jahr. Längere Zeiträume fordern Sie bitte in Abschnitten von je einem
      Jahr an. Wurde die Sicherheitseinrichtung im gewählten Zeitraum ausgetauscht, erhalten Sie mehrere
      Dateien („Teil 1 von 2“ …). Zusammen decken sie den Zeitraum vollständig ab.
    </p>

    <div class="mt-5 flex flex-wrap items-center gap-3">
      <button type="button"
              class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                     font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200
                     hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                     disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale"
              [disabled]="!canCreate()" (click)="askToCreate()">
        {{ creating() ? 'Wird angefordert …' : 'Export erstellen' }}
      </button>
      @if (!creating() && !periodComplete()) {
        <span class="text-xs font-semibold text-red-600">
          Bitte geben Sie ein gültiges Von- und Bis-Datum an.
        </span>
      } @else if (!creating() && !periodOrderValid()) {
        <span class="text-xs font-semibold text-red-600">
          Das Bis-Datum darf nicht vor dem Von-Datum liegen.
        </span>
      } @else if (!creating() && periodTooLong()) {
        <span class="text-xs font-semibold text-red-600">
          Der Zeitraum umfasst mehr als ein Jahr. Bitte grenzen Sie ihn ein.
        </span>
      }
    </div>
  </section>

  <!-- ── Bisherige Exporte ──────────────────────────────────────────────────────────────────────────── -->
  <section class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 class="text-base font-bold tracking-tight text-gray-900">Bisherige Exporte</h2>
      @if (live()) {
        <!-- Informational only. Every path on this screen works without the connection; it only removes a wait. -->
        <span class="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span class="h-2 w-2 rounded-full bg-green-500" aria-hidden="true"></span>
          Aktualisiert sich automatisch
        </span>
      }
    </div>

    @if (loading() && runs().length === 0) {
      <p class="text-sm text-gray-500">Exporte werden geladen …</p>
    } @else if (runs().length === 0) {
      <p class="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        Es wurde noch kein Export der Sicherheitseinrichtung erstellt. Wählen Sie oben einen Zeitraum und
        erstellen Sie den ersten.
      </p>
    } @else {
      @for (run of runs(); track run.id) {
        <legal-tse-export-run-card
          [run]="run"
          [detail]="openRunId() === run.id ? openRun() : null"
          [expanded]="openRunId() === run.id"
          [downloadingItemId]="downloadingItemId()"
          [retryingItemId]="retryingItemId()"
          (toggled)="toggle($event)"
          (download)="downloadItem($event)"
          (retry)="retryItem($event)"/>
      }
    }
  </section>
</section>

@if (confirming()) {
  <legal-tse-export-confirm-dialog
    [busy]="creating()"
    (confirmed)="create()"
    (cancelled)="cancelCreate()"/>
}
